import { useState } from 'react';

/**
 * Get the current URL search params.
 * Returns empty URLSearchParams on server-side.
 */
const getQuery = () => {
  if (typeof window !== 'undefined') {
    return new URLSearchParams(window.location.search);
  }
  return new URLSearchParams();
};

/**
 * Get a single query string value by key.
 */
const getQueryStringVal = (key: string): string | null => {
  return getQuery().get(key);
};

/**
 * Custom hook to read and update URL query parameters.
 * Preserves the value in state and syncs with browser URL.
 *
 * @param key - The query parameter key to read/write
 * @param defaultVal - Default value if the parameter is not present
 * @returns Tuple of [current value, setter function]
 *
 * @example
 * const [affiliateId] = useQueryParam('aid', '');
 * // URL: /quotation?aid=abc123
 * // affiliateId = 'abc123'
 */
const useQueryParam = (
  key: string,
  defaultVal: string,
): [string, (val: string) => void] => {
  const [query, setQuery] = useState(getQueryStringVal(key) || defaultVal);

  const updateUrl = (newVal: string) => {
    setQuery(newVal);

    const query = getQuery();

    if (newVal.trim() !== '') {
      query.set(key, newVal);
    } else {
      query.delete(key);
    }

    if (typeof window !== 'undefined') {
      const { protocol, pathname, host } = window.location;
      const newUrl = `${protocol}//${host}${pathname}?${query.toString()}`;
      window.history.pushState({}, '', newUrl);
    }
  };

  return [query, updateUrl];
};

export default useQueryParam;
